import {
  ElementType,
  forwardRef,
  ComponentPropsWithoutRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import classNames from './Anchor.module.scss';
import AppModal from '@/components/AppModal/AppModal';
import { DispatcherEvents, EventDispatcherContext } from '@/context/eventDispatcherContext';

export type AnchorPosition = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type TooltipPosition = {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
  width?: number;
  height?: number;
  insetInlineStart?: number;
  insetInlineEnd?: number;
};

export type TooltipOffset = {
  top?: number;
  left?: number;
};

interface AnchorProps {
  trigerType?: 'click' | 'hover' | 'none';
  autoClose?: boolean;
  stayInView?: boolean;
  tooltip?: any;
  renderTooltipPosition: (anchorPosition: AnchorPosition) => TooltipPosition;
  open?: boolean;
}

export type AnchorRefProps = {
  target: any;
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
};

export const createAnchorWrapper = <T extends ElementType>(Component: T) => {
  return forwardRef<AnchorRefProps, AnchorProps & ComponentPropsWithoutRef<T>>(
    (
      {
        trigerType = 'hover',
        autoClose = true,
        stayInView = false,
        tooltip,
        renderTooltipPosition,
        open = false,
        ...rest
      },
      ref
    ) => {
      const [isOpen, setIsOpen] = useState<boolean>(false);
      const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({});
      const tooltipPositionRef = useRef<TooltipPosition | null>(null);
      const anchorPositionRef = useRef<AnchorPosition | null>(null);
      const [tooltipOffset, setTooltipOffset] = useState<TooltipOffset>({});
      const componentRef = useRef<any>();
      const modalRef = useRef<HTMLDivElement | null>(null);
      const eventDispatcher = useContext(EventDispatcherContext);

      useEffect(() => {
        setIsOpen(open);
      }, [open]);

      useImperativeHandle(ref, () => {
        const _isOpen = isOpen;

        return {
          target: componentRef.current,
          open: () => {
            setIsOpen(true);
          },
          close: () => {
            setIsOpen(false);
          },
          isOpen: () => {
            return _isOpen;
          },
        };
      }, [isOpen]);

      useEffect(() => {
        let isActive = true;

        const calculatePosition = () => {
          const anchorElement =
            componentRef.current instanceof HTMLElement ? componentRef.current : null;

          if (!anchorElement) return;
          if (!modalRef.current) return;

          const anchorRect = anchorElement.getBoundingClientRect();

          const anchorPosition = {
            top: anchorRect.top,
            left: anchorRect.left,
            right: window.innerWidth - anchorRect.left - anchorRect.width,
            bottom: window.innerHeight - anchorRect.top - anchorRect.height,
            width: anchorRect.width,
            height: anchorRect.height,
          };

          if (
            tooltipPositionRef.current &&
            anchorPositionRef.current &&
            anchorPositionRef.current.top === anchorPosition.top &&
            anchorPositionRef.current.left === anchorPosition.left &&
            anchorPositionRef.current.right === anchorPosition.right &&
            anchorPositionRef.current.bottom === anchorPosition.bottom &&
            anchorPositionRef.current.width === anchorPosition.width &&
            anchorPositionRef.current.height === anchorPosition.height
          ) {
            return;
          }

          anchorPositionRef.current = anchorPosition;
          const tooltipPosition = renderTooltipPosition(anchorPosition);

          modalRef.current.style.top =
            tooltipPosition.top !== undefined ? tooltipPosition.top + 'px' : '';
          modalRef.current.style.left =
            tooltipPosition.left !== undefined ? tooltipPosition.left + 'px' : '';
          modalRef.current.style.right =
            tooltipPosition.right !== undefined ? tooltipPosition.right + 'px' : '';
          modalRef.current.style.bottom =
            tooltipPosition.bottom !== undefined ? tooltipPosition.bottom + 'px' : '';
          modalRef.current.style.width =
            tooltipPosition.width !== undefined ? tooltipPosition.width + 'px' : '';
          modalRef.current.style.height =
            tooltipPosition.height !== undefined ? tooltipPosition.height + 'px' : '';
          modalRef.current.style.insetInlineStart =
            tooltipPosition.insetInlineStart !== undefined
              ? tooltipPosition.insetInlineStart + 'px'
              : '';
          modalRef.current.style.insetInlineEnd =
            tooltipPosition.insetInlineEnd !== undefined
              ? tooltipPosition.insetInlineEnd + 'px'
              : '';

          if (
            tooltipPositionRef.current &&
            tooltipPositionRef.current.top === tooltipPosition.top &&
            tooltipPositionRef.current.left === tooltipPosition.left &&
            tooltipPositionRef.current.right === tooltipPosition.right &&
            tooltipPositionRef.current.bottom === tooltipPosition.bottom &&
            tooltipPositionRef.current.width === tooltipPosition.width &&
            tooltipPositionRef.current.height === tooltipPosition.height &&
            tooltipPositionRef.current.insetInlineStart === tooltipPosition.insetInlineStart &&
            tooltipPositionRef.current.insetInlineEnd === tooltipPosition.insetInlineEnd
          ) {
            return;
          }

          tooltipPositionRef.current = tooltipPosition;
          setTooltipPosition(tooltipPosition);
        };

        const loop = () => {
          if (!isActive) {
            return;
          }

          if (!isOpen) {
            anchorPositionRef.current = null;
            tooltipPositionRef.current = null;
            setTooltipPosition({});
            setTooltipOffset({});
            return;
          }

          calculatePosition();
          requestAnimationFrame(loop);
        };

        loop();

        return () => {
          isActive = false;
        };
      }, [isOpen, renderTooltipPosition]);

      useEffect(() => {
        if (!isOpen) {
          return;
        }

        const handleBackdropShown = () => {
          setIsOpen(false);
        };

        eventDispatcher.addListener(DispatcherEvents.APP_BACKDROP_SHOWN, handleBackdropShown);

        return () => {
          eventDispatcher.removeListener(DispatcherEvents.APP_BACKDROP_SHOWN, handleBackdropShown);
        };
      }, [isOpen, eventDispatcher]);

      useEffect(() => {
        if (isOpen && autoClose) {
          const handleOutsideClick = (event: any) => {
            if (trigerType === 'click' && componentRef.current?.contains(event.target)) {
              return;
            }

            if (modalRef.current && !modalRef.current.contains(event.target)) {
              setIsOpen(false);
            }
          };

          document.addEventListener('mousedown', handleOutsideClick);
          document.addEventListener('touchstart', handleOutsideClick);

          return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
          };
        }
      }, [isOpen, autoClose]);

      useEffect(() => {
        const calculateOffset = () => {
          const modalElement = modalRef.current;
          if (!modalElement) return;

          const offset: TooltipOffset = {};
          const modalRect = modalElement.getBoundingClientRect();
          const viewWidth = window.innerWidth;
          const viewHeight = window.innerHeight;

          if (modalRect.left < 0) {
            offset.left = 10 - modalRect.left;
          } else if (modalRect.right > viewWidth) {
            offset.left = viewWidth - modalRect.right - 10;
          }

          if (modalRect.top < 0) {
            offset.top = 10 - modalRect.top;
          } else if (modalRect.bottom > viewHeight) {
            offset.top = viewHeight - modalRect.bottom - 10;
          }

          setTooltipOffset(offset);
        };

        if (isOpen && stayInView) {
          const timer = setTimeout(calculateOffset, 50);

          return () => {
            clearTimeout(timer);
          };
        }
      }, [isOpen, stayInView, tooltipPosition]);

      const handleClick = useCallback(
        (e: any) => {
          if ((rest as any)?.disabled) {
            return;
          }

          rest?.onClick?.(e);

          if (trigerType !== 'click') {
            return;
          }

          setIsOpen(isOpen => !isOpen);
        },
        [trigerType, rest]
      );

      const handleMouseEnter = useCallback(() => {
        if (trigerType !== 'hover') {
          return;
        }

        setIsOpen(true);
      }, [trigerType]);

      const handleMouseLeave = useCallback(() => {
        if (trigerType !== 'hover') {
          return;
        }

        setIsOpen(false);
      }, [trigerType]);

      return (
        <>
          <Component
            ref={componentRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...(rest as any)}
            onClick={handleClick}
          />
          {isOpen && (
            <AppModal
              showBackdrop={false}
              type='fade'
              show={isOpen}
              className={classNames.modal}
              modalContentClassName={classNames.modal}
            >
              <div ref={modalRef} className={classNames.tooltip}>
                <div className={classNames.offset} style={tooltipOffset}>
                  {tooltip}
                </div>
              </div>
            </AppModal>
          )}
        </>
      );
    }
  );
};
